import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import BrandLockup from './BrandLockup';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import { usePlanLabel } from '../i18n/usePlanLabel';
import { appTheme } from '../styles/theme';
import { Button } from './ui/Button';

/** Routes with their own chrome (full-screen carousel, editor workspace). */
const HIDDEN_ROUTES = new Set(['/preview', '/edit']);

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid ${appTheme.border};
  background: rgba(250, 250, 248, 0.92);
  backdrop-filter: blur(12px);
`;

const Inner = styled.div`
  max-width: ${appTheme.maxWidth};
  margin: 0 auto;
  padding: 0 24px;
  min-height: 68px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    padding: 12px 16px;
    min-height: auto;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  justify-self: start;

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
    border-radius: ${appTheme.radiusSm};
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-self: center;

  @media (max-width: 768px) {
    order: 3;
    width: 100%;
    justify-content: center;
  }
`;

const NavItem = styled(RouterNavLink)`
  padding: 8px 14px;
  border-radius: ${appTheme.radiusPill};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${appTheme.textMuted};
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${appTheme.surfaceHover};
    color: ${appTheme.text};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }

  &.active {
    color: ${appTheme.text};
    font-weight: 600;
  }
`;

const AccountArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-self: end;

  @media (max-width: 768px) {
    margin-left: auto;
  }
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const Initial = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${appTheme.green};
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 700;
`;

const UserName = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.text};
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.15s ease;

  @media (max-width: 640px) {
    display: none;
  }
`;

const ProfileTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${appTheme.radiusPill};

  &:hover ${UserName} {
    color: ${appTheme.green};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const DrawerOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(10, 46, 40, 0.35);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.25s ease;
`;

const AccountDrawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 210;
  width: min(400px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  border-left: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowLg};
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
`;

const DrawerUser = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const DrawerAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const DrawerInitial = styled(Initial)`
  width: 44px;
  height: 44px;
  font-size: 1rem;
  flex-shrink: 0;
`;

const DrawerUserText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const DrawerName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${appTheme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlanTag = styled(Link)`
  align-self: flex-start;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.green};
  background: ${appTheme.mint};
  border-radius: ${appTheme.radiusPill};
  padding: 5px 10px;
  text-decoration: none;

  &:hover {
    background: #dceedf;
  }
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.surface};
  color: ${appTheme.textMuted};
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${appTheme.surfaceHover};
    color: ${appTheme.text};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: auto;
`;

const DrawerLink = styled(Link)`
  display: block;
  padding: 12px 14px;
  border-radius: ${appTheme.radiusMd};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${appTheme.text};
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: ${appTheme.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const DrawerFooter = styled.div`
  padding-top: 24px;
  border-top: 1px solid ${appTheme.border};
  margin-top: 24px;
`;

const AppHeader: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout, openLogin } = useAuth();
  const { pathname } = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const planLabel = usePlanLabel(user && user !== null ? user.plan : null, true);

  const closeAccount = useCallback(() => {
    setAccountOpen(false);
  }, []);

  useEffect(() => {
    closeAccount();
  }, [pathname, closeAccount]);

  useEffect(() => {
    if (!accountOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeAccount();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [accountOpen, closeAccount]);

  if (HIDDEN_ROUTES.has(pathname)) {
    return null;
  }

  const renderAccountTrigger = () => {
    if (user === undefined) {
      return null;
    }
    if (user === null) {
      return (
        <Button type="button" $variant="ghost" $size="sm" onClick={() => openLogin()}>
          {t('nav.signIn')}
        </Button>
      );
    }

    return (
      <ProfileTrigger
        ref={triggerRef}
        type="button"
        aria-expanded={accountOpen}
        aria-haspopup="dialog"
        aria-controls="account-drawer"
        onClick={() => setAccountOpen(true)}
      >
        {user.avatarUrl ? (
          <Avatar src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />
        ) : (
          <Initial aria-hidden>{user.name.charAt(0).toUpperCase()}</Initial>
        )}
        <UserName>{user.name}</UserName>
      </ProfileTrigger>
    );
  };

  const renderAccountDrawer = () => {
    if (!user || user === null) {
      return null;
    }

    return createPortal(
      <>
        <DrawerOverlay $open={accountOpen} onClick={closeAccount} aria-hidden={!accountOpen} />
        <AccountDrawer
          ref={drawerRef}
          id="account-drawer"
          $open={accountOpen}
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-drawer-title"
        >
          <DrawerHeader>
            <DrawerUser>
              {user.avatarUrl ? (
                <DrawerAvatar src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />
              ) : (
                <DrawerInitial aria-hidden>{user.name.charAt(0).toUpperCase()}</DrawerInitial>
              )}
              <DrawerUserText>
                <DrawerName id="account-drawer-title">{user.name}</DrawerName>
                <PlanTag to="/subscription" onClick={closeAccount}>
                  {planLabel}
                </PlanTag>
              </DrawerUserText>
            </DrawerUser>
            <CloseButton type="button" aria-label={t('nav.closeAccountMenu')} onClick={closeAccount}>
              ×
            </CloseButton>
          </DrawerHeader>

          <DrawerNav aria-label={t('nav.account')}>
            <DrawerLink to="/profile" onClick={closeAccount}>
              {t('nav.profileSettings')}
            </DrawerLink>
          </DrawerNav>

          <DrawerFooter>
            <Button
              type="button"
              $variant="secondary"
              $size="md"
              onClick={() => {
                closeAccount();
                logout();
              }}
              style={{ width: '100%' }}
            >
              {t('nav.signOut')}
            </Button>
          </DrawerFooter>
        </AccountDrawer>
      </>,
      document.body,
    );
  };

  return (
    <>
      <Bar>
        <Inner>
          <Brand to="/" aria-label={t('nav.homeAria')}>
            <BrandLockup logoHeight={30} />
          </Brand>
          <Nav aria-label={t('nav.primary')}>
            <NavItem to="/" end>
              {t('nav.templates')}
            </NavItem>
            <NavItem to="/decks" end>
              {t('nav.myDecks')}
            </NavItem>
            <NavItem to="/subscription" end>
              {t('nav.pricing')}
            </NavItem>
          </Nav>
          <AccountArea>
            <LanguageSwitcher compact />
            {renderAccountTrigger()}
          </AccountArea>
        </Inner>
      </Bar>
      {renderAccountDrawer()}
    </>
  );
};

export default AppHeader;
